/**
 * Appointments Hook
 * Feature: 005-clinic-scheduling
 *
 * Operations for one-off appointment creation, session cancellation, and rescheduling.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/features/auth'
import type {
  Session,
  CreateOneOffAppointmentInput,
  CancelSessionInput,
  RescheduleSessionInput,
  ScheduleOperationResult,
} from '../types/scheduling.types'

function normalizeLocationText(
  value: string | null | undefined
): string | null {
  const normalized = value?.trim()
  return normalized ? normalized : null
}

async function fetchOneOffAppointments(
  organizationId: string,
  patientId: string
): Promise<Session[]> {
  const today = new Date().toLocaleDateString('en-CA')

  const { data, error } = await supabase
    .from('sessions')
    .select(
      `
      *,
      primary_professional:profiles!sessions_professional_id_fkey (
        id,
        full_name
      ),
      additional_professionals:session_professionals (
        id,
        session_id,
        professional_id,
        created_at,
        professional:profiles!session_professionals_professional_fkey (
          id,
          full_name
        )
      )
    `
    )
    .eq('organization_id', organizationId)
    .eq('patient_id', patientId)
    .is('recurring_schedule_id', null)
    .in('status', ['scheduled', 'confirmed'])
    .gte('session_date', today)
    .order('session_date', { ascending: true })
    .order('start_time', { ascending: true })

  if (error) throw error

  return (data ?? []).map((session) => ({
    ...session,
    primary_professional: session.primary_professional
      ? {
          ...(session.primary_professional as {
            id: string
            full_name: string
          }),
          specialty: 'Profissional de Saúde',
        }
      : undefined,
    additional_professionals: (session.additional_professionals ?? []).map(
      (sp: {
        id: string
        session_id: string
        professional_id: string
        created_at: string
        professional?: { id: string; full_name: string } | null
      }) => ({
        ...sp,
        professional: sp.professional
          ? {
              ...sp.professional,
              specialty: 'Profissional de Saúde',
            }
          : undefined,
      })
    ),
  })) as Session[]
}

export function useOneOffAppointments(patientId?: string) {
  const { currentOrganization } = useAuth()
  const organizationId = currentOrganization?.id

  return useQuery({
    queryKey: ['sessions', organizationId, patientId, 'one-off'],
    queryFn: () => fetchOneOffAppointments(organizationId!, patientId!),
    enabled: !!organizationId && !!patientId,
  })
}

// ============================================================
// Create One-Off Appointment
// ============================================================

export function useCreateOneOffAppointment() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useAuth()

  return useMutation({
    mutationFn: async (
      input: CreateOneOffAppointmentInput
    ): Promise<ScheduleOperationResult<Session>> => {
      const organizationId = currentOrganization!.id
      const primaryProfessionalId = input.professional_ids[0]

      // 1. Create the session
      const { data: session, error: sessionError } = await supabase
        .from('sessions')
        .insert({
          organization_id: organizationId,
          patient_id: input.patient_id,
          professional_id: primaryProfessionalId,
          session_date: input.session_date,
          start_time: input.start_time,
          end_time: input.end_time,
          session_type: input.session_type,
          status: 'scheduled',
          location_text: normalizeLocationText(input.location_text),
          notes: input.notes ?? null,
          recurring_schedule_id: null,
          is_exception: false,
        })
        .select()
        .single()

      if (sessionError) {
        return { success: false, error: sessionError.message }
      }

      // 2. Add additional professionals to session_professionals
      if (input.professional_ids.length > 1) {
        const sessionProfessionals = input.professional_ids
          .slice(1)
          .map((profId) => ({
            session_id: session.id,
            professional_id: profId,
          }))

        const { error: spError } = await supabase
          .from('session_professionals')
          .insert(sessionProfessionals)

        if (spError) {
          return { success: false, error: spError.message }
        }
      }

      return { success: true, data: session as Session }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] })
      queryClient.invalidateQueries({ queryKey: ['sessions'] })
    },
  })
}

// ============================================================
// Cancel Session
// ============================================================

export function useCancelSession() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (
      input: CancelSessionInput
    ): Promise<ScheduleOperationResult> => {
      if (input.scope === 'this_only') {
        // Cancel just this one session
        const { error } = await supabase
          .from('sessions')
          .update({ status: 'cancelled', is_exception: true })
          .eq('id', input.session_id)

        if (error) return { success: false, error: error.message }
      } else {
        // Cancel this and all future sessions from the same recurring schedule
        const { data: session, error: fetchError } = await supabase
          .from('sessions')
          .select('recurring_schedule_id, session_date')
          .eq('id', input.session_id)
          .single()

        if (fetchError) return { success: false, error: fetchError.message }

        if (session.recurring_schedule_id) {
          // Cancel all future sessions from this recurring schedule
          const { error: cancelError } = await supabase
            .from('sessions')
            .update({ status: 'cancelled' })
            .eq('recurring_schedule_id', session.recurring_schedule_id)
            .gte('session_date', session.session_date)
            .eq('status', 'scheduled')

          if (cancelError) {
            return { success: false, error: cancelError.message }
          }

          // Optionally set effective_until on the recurring schedule
          const { error: scheduleError } = await supabase
            .from('recurring_schedules')
            .update({ effective_until: session.session_date })
            .eq('id', session.recurring_schedule_id)

          if (scheduleError) {
            return { success: false, error: scheduleError.message }
          }
        } else {
          // One-off appointment — just cancel this session
          const { error } = await supabase
            .from('sessions')
            .update({ status: 'cancelled' })
            .eq('id', input.session_id)

          if (error) return { success: false, error: error.message }
        }
      }

      return { success: true }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] })
      queryClient.invalidateQueries({ queryKey: ['sessions'] })
      queryClient.invalidateQueries({ queryKey: ['recurring-schedules'] })
    },
  })
}

// ============================================================
// Reschedule Session
// ============================================================

export function useRescheduleSession() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (
      input: RescheduleSessionInput
    ): Promise<ScheduleOperationResult<Session>> => {
      // Fetch current session to store original values
      const { data: currentSession, error: fetchError } = await supabase
        .from('sessions')
        .select('session_date, start_time, end_time, recurring_schedule_id')
        .eq('id', input.session_id)
        .single()

      if (fetchError) return { success: false, error: fetchError.message }

      if (input.scope === 'this_only') {
        // Reschedule just this one session
        const { data: updated, error } = await supabase
          .from('sessions')
          .update({
            session_date: input.new_date,
            start_time: input.new_start_time,
            end_time: input.new_end_time,
            is_exception: true,
            original_date: currentSession.session_date,
            original_start_time: currentSession.start_time,
            original_end_time: currentSession.end_time,
          })
          .eq('id', input.session_id)
          .select()
          .single()

        if (error) return { success: false, error: error.message }
        return { success: true, data: updated as Session }
      } else {
        // Reschedule this and all future sessions
        if (currentSession.recurring_schedule_id) {
          // Update the recurring schedule rule
          const newDayOfWeek = new Date(input.new_date).getDay()

          const { error: scheduleError } = await supabase
            .from('recurring_schedules')
            .update({
              day_of_week: newDayOfWeek,
              start_time: input.new_start_time,
              end_time: input.new_end_time,
            })
            .eq('id', currentSession.recurring_schedule_id)

          if (scheduleError) {
            return { success: false, error: scheduleError.message }
          }

          // Update all future non-exception sessions
          const { error: sessionsError } = await supabase
            .from('sessions')
            .update({
              start_time: input.new_start_time,
              end_time: input.new_end_time,
            })
            .eq('recurring_schedule_id', currentSession.recurring_schedule_id)
            .eq('is_exception', false)
            .gte('session_date', currentSession.session_date)
            .eq('status', 'scheduled')

          if (sessionsError) {
            return { success: false, error: sessionsError.message }
          }

          // Update the triggering session specifically (may have different date)
          const { data: updated, error: updateError } = await supabase
            .from('sessions')
            .update({
              session_date: input.new_date,
              start_time: input.new_start_time,
              end_time: input.new_end_time,
              is_exception: true,
              original_date: currentSession.session_date,
              original_start_time: currentSession.start_time,
              original_end_time: currentSession.end_time,
            })
            .eq('id', input.session_id)
            .select()
            .single()

          if (updateError) {
            return { success: false, error: updateError.message }
          }

          return { success: true, data: updated as Session }
        } else {
          // One-off appointment — just reschedule this session
          const { data: updated, error } = await supabase
            .from('sessions')
            .update({
              session_date: input.new_date,
              start_time: input.new_start_time,
              end_time: input.new_end_time,
              original_date: currentSession.session_date,
              original_start_time: currentSession.start_time,
              original_end_time: currentSession.end_time,
            })
            .eq('id', input.session_id)
            .select()
            .single()

          if (error) return { success: false, error: error.message }
          return { success: true, data: updated as Session }
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] })
      queryClient.invalidateQueries({ queryKey: ['sessions'] })
      queryClient.invalidateQueries({ queryKey: ['recurring-schedules'] })
    },
  })
}
